import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Palmtree } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useLang, useL } from "@/i18n";
import { homeQueryKey, fetchHome, productsByTagQueryKey, fetchProductsByTag } from "@/lib/queries";
import { cldUrl } from "@/lib/img";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading, ProductCard } from "@/components/shared";

function CategorySkeleton() {
  return (
    <div className="flex shrink-0 flex-col items-center gap-3">
      <Skeleton className="h-24 w-24 rounded-full sm:h-28 sm:w-28" />
      <Skeleton className="h-4 w-16" />
    </div>
  );
}

export function ShopByCategory() {
  const { t, lang } = useLang();
  const L = useL();
  const scroller = useRef(null);
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: homeQueryKey, queryFn: fetchHome });
  const cats = data?.categories || [];

  return (
    <section className="mx-auto max-w-[1400px] px-4 py-16" data-testid="shop-by-category">
      <SectionHeading
        overline={<span className="flex items-center gap-2"><Palmtree size={14} /> {t("home.pwodwi")}</span>}
        title={t("home.categories")}
        action={<Link to="/shop" className="flex items-center gap-1.5 text-sm font-semibold text-sunset hover:gap-2.5 transition-all" data-testid="view-all-categories">{lang === "fr" ? "Voir toutes les catégories" : "View all categories"} <ArrowRight size={15} /></Link>}
      />
      {isError ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center text-sm text-muted-foreground" data-testid="categories-error">
          <p>{t("common.loadError")}</p>
          <button onClick={() => refetch()} className="rounded-full border border-border px-4 py-2 font-semibold text-navy transition hover:border-gold">{t("common.retry")}</button>
        </div>
      ) : (
        <div ref={scroller} className="no-scrollbar flex gap-5 overflow-x-auto pb-4 sm:grid sm:grid-cols-4 sm:overflow-visible lg:grid-cols-6">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => <CategorySkeleton key={i} />)
            : cats.map((c) => (
              <Link key={c.id} to={`/shop?category=${c.slug}`} data-testid={`category-card-${c.slug}`} className="group flex shrink-0 flex-col items-center gap-3 text-center">
                <div className="relative h-24 w-24 overflow-hidden rounded-full ring-2 ring-transparent transition-all duration-300 group-hover:ring-gold group-hover:ring-offset-2 group-hover:ring-offset-cream sm:h-28 sm:w-28">
                  <img src={cldUrl(c.image, { w: 224 })} alt={L(c, "name")} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-navy/10 transition group-hover:bg-navy/0" />
                </div>
                <span className="max-w-[7rem] font-head text-sm font-semibold text-navy transition group-hover:text-sunset">{L(c, "name")}</span>
              </Link>
            ))}
        </div>
      )}
    </section>
  );
}

const TABS = [
  { key: "featured", tag: "featured", label: "home.featured" },
  { key: "bestsellers", tag: "bestseller", label: "home.bestsellers" },
  { key: "new", tag: "new", label: "home.newArrivals" },
  { key: "offers", tag: "special", label: "home.offers" },
  { key: "pwodwi", tag: "pwodwi-lakay", label: "home.pwodwi" },
];

function ProductCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border bg-cream-card">
      <Skeleton className="aspect-square w-full rounded-none" />
      <div className="space-y-2 p-4">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-5 w-16" />
      </div>
    </div>
  );
}

export function ProductTabs() {
  const { t } = useLang();
  const [active, setActive] = useState("featured");
  const tag = TABS.find((x) => x.key === active).tag;

  // The "featured" tab's data is already part of the combined /home payload
  // fetched by Hero/ShopByCategory/etc, so it's read straight from that
  // cache instead of firing a redundant request. Other tabs fetch (and
  // cache, per-tag) independently only when actually selected.
  const homeQuery = useQuery({ queryKey: homeQueryKey, queryFn: fetchHome });
  const tagQuery = useQuery({
    queryKey: productsByTagQueryKey(tag),
    queryFn: () => fetchProductsByTag(tag),
    enabled: tag !== "featured",
  });

  const activeQuery = tag === "featured" ? homeQuery : tagQuery;
  const products = tag === "featured" ? (homeQuery.data?.featured_products || []) : (tagQuery.data || []);
  const isLoading = activeQuery.isLoading;
  const isError = activeQuery.isError;

  return (
    <section className="bg-cream-card py-16" data-testid="product-tabs">
      <div className="mx-auto max-w-[1400px] px-4">
        <div className="mb-8 flex flex-col items-center gap-6 text-center">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-sunset">Kreyola Boutik</p>
            <h2 className="font-display text-3xl font-bold text-navy sm:text-4xl">{t("home.featured")}</h2>
          </div>
          <div className="no-scrollbar flex max-w-full gap-2 overflow-x-auto">
            {TABS.map((tab) => (
              <button key={tab.key} onClick={() => setActive(tab.key)} data-testid={`tab-${tab.key}`}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition ${active === tab.key ? "btn-gold" : "border border-border bg-cream text-navy hover:border-gold"}`}>
                {t(tab.label)}
              </button>
            ))}
          </div>
        </div>
        {isLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <ProductCardSkeleton key={i} />)}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center text-muted-foreground" data-testid="products-error">
            <p>{t("common.loadError")}</p>
            <button onClick={() => activeQuery.refetch()} className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-navy transition hover:border-gold">{t("common.retry")}</button>
          </div>
        ) : products.length === 0 ? (
          <p className="py-10 text-center text-muted-foreground">{t("shop.noResults")}</p>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>
    </section>
  );
}

export function Storytelling() {
  const { lang } = useLang();
  const { data, isLoading } = useQuery({ queryKey: homeQueryKey, queryFn: fetchHome });
  const c = data?.content;

  if (isLoading) {
    return (
      <section className="mx-auto max-w-[1400px] px-4 py-16" data-testid="storytelling">
        <div className="space-y-8">
          {[0, 1].map((i) => <Skeleton key={i} className="h-64 rounded-3xl md:h-[340px]" />)}
        </div>
      </section>
    );
  }
  if (!c) return null;
  const blocks = [
    { img: c.story_image, title: c[`story_title_${lang}`], body: c[`story_body_${lang}`], reverse: false },
    { img: c.story2_image, title: c[`story2_title_${lang}`], body: c[`story2_body_${lang}`], reverse: true },
  ];
  return (
    <section className="mx-auto max-w-[1400px] px-4 py-16" data-testid="storytelling">
      <div className="space-y-8">
        {blocks.map((b, i) => (
          <div key={i} className={`grid items-center gap-8 overflow-hidden rounded-3xl bg-navy card-shadow-lg md:grid-cols-2 ${b.reverse ? "md:[&>div:first-child]:order-2" : ""}`}>
            <div className="relative h-64 md:h-full md:min-h-[340px]">
              <img src={cldUrl(b.img, { w: 1400 })} alt={b.title} loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent md:bg-gradient-to-r" />
            </div>
            <div className="p-8 sm:p-12">
              <p className="mb-3 font-script text-3xl text-gold">{b.title}</p>
              <p className="text-base leading-relaxed text-cream/80">{b.body}</p>
              <Link to="/about" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-gold transition hover:gap-3">
                {lang === "fr" ? "Notre histoire" : "Our story"} <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const REVIEWS = [
  { name: "Nadège P.", city: "Brooklyn, NY", fr: "Enfin des produits qui ont vraiment le goût de lakay ! Livraison rapide et emballage soigné.", en: "Finally products that truly taste like home! Fast delivery and careful packaging." },
  { name: "Jean R.", city: "Miami, FL", fr: "Le café Rebo me rappelle mon enfance. Qualité exceptionnelle, je recommande.", en: "The Rebo coffee reminds me of my childhood. Exceptional quality, highly recommend." },
  { name: "Chef Marco", city: "Boston, MA", fr: "Pour mon restaurant, l'approvisionnement en épis ayisyen est fiable et authentique.", en: "For my restaurant, the supply of Haitian seasoning is reliable and authentic." },
];

export function Testimonials() {
  const { lang } = useLang();
  return (
    <section className="mx-auto max-w-[1400px] px-4 py-16" data-testid="testimonials">
      <SectionHeading center overline={lang === "fr" ? "Notre communauté" : "Our community"} title={lang === "fr" ? "Aimé par la diaspora" : "Loved by the diaspora"} />
      <div className="grid gap-6 md:grid-cols-3">
        {REVIEWS.map((r, i) => (
          <div key={i} className="rounded-2xl border border-border bg-cream-card p-7 card-shadow">
            <div className="mb-3 flex gap-0.5 text-gold">{"★★★★★"}</div>
            <p className="text-sm leading-relaxed text-navy/80">"{lang === "fr" ? r.fr : r.en}"</p>
            <div className="mt-5 flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-full bg-navy font-head text-sm font-bold text-gold">{r.name[0]}</div>
              <div>
                <p className="font-head text-sm font-semibold text-navy">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.city}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
